import React, { useContext, useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router';
import { store } from 'components/Store';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useAuth0 } from '@auth0/auth0-react';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import parliamentarianUsers from './parliamentarianUsers';
import getAppSite, { useSiteSectionsNames } from 'utils/getAppSite';
import { contentItemSearchQuery } from 'components/Dashboard/helpers/locationSearchDescriptor';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import array_move from 'components/Dashboard/helpers/arrayMove';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { DisabledTwitterPopup, ParliamentLivePopup } from 'components/Dashboard/Navigation/NotFeaturePopups';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import ConfigData from 'config/ConfigData';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import MarkAsReadButton from 'components/Dashboard/SidebarElements/MarkAsReadButton';
import { BottomTermsAndConditions } from 'components/Dashboard/Navigation/NavigationSideBar';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';
import useTranscriptsFeatureEnabled from 'components/Dashboard/hooks/useTranscriptsFeatureEnabled';

const DashboardSidebar = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { code } = activePlan;
  const isMobile = useIsMobile();

  const [heightContainer, containerRef] = useHeightContainer();
  const { isAuthenticated } = useAuth0();

  return (
    <div className={`main-navigation ${state.isHiddenMenu ? '' : 'show-menu'}`}>
      <div
        className={`intercom-main-navigation pt-lg-4 ${isAuthenticated ? 'pt-5' : ''}`}
        data-intercom-target='Side bar element'
      >
        <div className={`${parliamentarianUsers(code) ? 'navigation-parliamentarians' : ''}`}>
          <div ref={containerRef} />

          <CustomScrollbar
            style={{ height: `${heightContainer}px` }}
            maximalThumbYSize={100}
            className={'simple-scrollbar'}
          >
            <div
              className={`pl-lg-4 navigation-container d-flex justify-content-between flex-column ${
                isMobile ? 'navigation-container-mobile-logged-out' : ''
              }`}
            >
              <div>
                {!isMobile && (
                  <div className={`sidebar-nav`}>
                    <ul className={`px-0 mb-0 overflow-hidden sidenav-list`}>
                      <SideBarItems />
                    </ul>
                  </div>
                )}
              </div>

              <BottomTermsAndConditions />
            </div>
          </CustomScrollbar>
        </div>
      </div>
    </div>
  );
};

const SideBarItems = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { unseenNumbersCurrentList, activePlan, totalNumbers, search, customContactsOnSearch, firstLoadTopic } = state;
  const { list } = search;
  const { enableTwitter, enableLiveTranscription, code } = activePlan;

  const { closeMenu } = props;
  const [isOpenLivePopup, setIsOpenLivePopup] = useState(false);
  const [showTwitterPopup, setShowTwitterPopup] = useState(false);
  const params = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  const { inboxSection, searchResultsSection } = useMainSectionParameter(params);
  const { getName } = useSiteSectionsNames();
  const { showDashboard, generalAllowedDashboardUsers } = useDashboardUrlCondition();

  const locationDescriptor = (path, influence) => {
    let locationSearch = contentItemSearchQuery(location, list);
    const locationSearchParams = new URLSearchParams(locationSearch); //This always will return topic-id or search
    if (
      locationSearch === '' &&
      location.pathname?.includes('/transcripts') &&
      !!firstLoadTopic &&
      firstLoadTopic !== 'noListsCreated'
    ) {
      locationSearchParams.set('topic-id', firstLoadTopic?.id === null ? 'all' : firstLoadTopic?.id);
    }

    const searchParams = new URLSearchParams(location.search); //Here we need params like mode=rate-updates that are not included in contentItemSearchQuery
    const merged = new URLSearchParams(locationSearchParams);
    if (getUrlParam('returnUrl')) {
      searchParams.set('returnUrl', getUrlParam('returnUrl'));
    }

    const startDateTimeParam = inboxSection ? getUrlParam('startDateTime') : null;
    const minutesPerCallParam = inboxSection ? getUrlParam('minutesPerCall') : null;
    if (startDateTimeParam) {
      searchParams.set('startDateTime', startDateTimeParam);
    }
    if (minutesPerCallParam) {
      searchParams.set('minutesPerCall', minutesPerCallParam);
    }
    searchParams.delete('sort');

    searchParams.forEach((value, key) => {
      if (!merged.has(key)) {
        merged.append(key, value);
      }
    });
    return {
      pathname: `/${
        influence ? 'influence' : `${params?.mainSection ?? (isAuthenticated ? 'reference' : 'monitor')}`
      }${path}`,
      search: merged.toString(),
      hash: '',
    };
  };

  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  let items = [
    {
      name: 'Key updates',
      type: 'KeyUpdate',
      className: 'news',
      unseenNumber: ['KeyUpdate'],
      label: getName({ property: 'Key updates' }),
    },
    {
      name: 'Consultations',
      type: 'Consultation',
      className: 'consultation',
      unseenNumber: ['Consultation'],
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Legislation',
      unseenNumber: ['Legislation'],
    },
    {
      name: 'Parliamentary record',
      type: 'ParliamentaryRecord',
      className: 'parliamentary',
      unseenNumber: ['ParliamentaryRecord', 'HansardContent'],
      label: getName({ property: 'Parliamentary record' }),
      icon: ConfigData.icons.parliament,
    },
    {
      name: 'Committee publications',
      type: 'CommitteePublication',
      className: 'parliamentary',
      unseenNumber: ['CommitteePublications'],
      nameForUnseenNumber: 'CommitteePublications',
      icon: 'document',
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Library material',
      type: 'LibraryArticle',
      icon: 'library-material',
      unseenNumber: ['LibraryArticle'],
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Government publications',
      type: 'GovernmentPublication',
      icon: 'crown',
      unseenNumber: ['GovernmentPublication'],
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Upcoming events',
      type: 'Event',
      icon: 'calendar',
      unseenNumber: ['Event'],
      hideOnSearch: true,
    },
    {
      name: 'Social media',
      type: 'tweet',
      className: 'social',
      icon: 'twitter-x',
      unseenNumber: ['Tweet'],
      padlock: !enableTwitter,
      onClick: !enableTwitter
        ? () => {
            setShowTwitterPopup(true);
          }
        : undefined,
      hidden: !showTweets,
    },
    {
      name: 'Regulations',
      type: 'Regulation',
      unseenNumber: ['RegulationDocument'],
      nameForUnseenNumber: 'Regulations',
      icon: 'document',
      hidden: getAppSite() === 'global',
    },
    {
      name: 'Stakeholders',
      unseenNumber: [''],
      icon: 'team',
      hideOnInbox: true,
      hideOnSearch: true,
    },
    {
      name: 'Lobbying material',
      type: 'UserContent',
      icon: 'briefing-material',
      unseenNumber: ['UserContent'],
      hidden: getAppSite() === 'usa',
    },
  ];

  if (parliamentarianUsers(code)) {
    const position = items.findIndex((item) => item.name === 'Lobbying material');
    items[position].name = 'Campaigns';
    items = array_move(items, position, 1);
  }

  if (getAppSite() === 'usa') {
    const position = items.findIndex((item) => item.name === 'Stakeholders');
    items = array_move(items, position, 4);
    const positionParliamentaryRecord = items.findIndex((item) => item.name === 'Parliamentary record');
    items = array_move(items, positionParliamentaryRecord, 2);
  }

  if (customContactsOnSearch) {
    const stakeholdersPosition = items.findIndex((item) => item.name === 'Stakeholders');
    items.splice(stakeholdersPosition + 1, 0, {
      name: 'Custom contacts',
      type: 'CrmContact',
      nameForUnseenNumber: 'CrmContact',
      icon: 'directory',
      hideOnInbox: true,
      unseenNumber: ['CrmContact'],
    });
  }

  if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'false') {
    let twitterPosition = items.findIndex((item) => item.name === 'Social media');
    items.splice(twitterPosition, 1);
  }

  if (!enableTwitter) {
    const socialMediaPosition = items.findIndex((item) => item.name === 'Social media');
    if (socialMediaPosition !== -1) {
      items = array_move(items, socialMediaPosition, items.length - 1);
    }
  }

  const isMobile = useIsMobile();
  const { rateUpdatesMode } = useDetectRateMode();
  const { transcriptsFeatureEnabled } = useTranscriptsFeatureEnabled();
  const transcriptsEnabled = inboxSection && transcriptsFeatureEnabled;

  return (
    <>
      {import.meta.env.VITE_APP_SHOW_PRIORITY_UPDATES === 'true' && showDashboard && !searchResultsSection && (
        <>
          <h4 className={`title-h5-m-bold pl-3 my-2 mb-lg-3 `}>Today’s highlights</h4>
          <li className='d-block'>
            <NavLink
              to={locationDescriptor(`/priority-updates`)}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              onClick={(e) => {
                if (closeMenu) {
                  closeMenu();
                }
              }}
            >
              <div className='d-flex'>
                <div className={`sidebar-item-container sidebar-priority`}>
                  <span className={'political-updates-nav-icon icon-priority inline-block '} />
                </div>
                <span>Priority updates</span>
              </div>
            </NavLink>
          </li>
        </>
      )}
      {getAppSite() === 'global' && (
        <>
          <div className={`d-block mb-3 ${showDashboard ? '' : ' mt-2'}`}>
            <li>
              <NavLink
                to={locationDescriptor(`/parliament-live`)}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''} ${rateUpdatesMode ? 'light-50-text cursor-auto' : ''}`
                }
                onClick={(e) => {
                  if (rateUpdatesMode) {
                    preventLink(e);
                  }
                  if (closeMenu && (!isMobile || (isMobile && enableLiveTranscription))) {
                    closeMenu();
                  }
                  if (!enableLiveTranscription) {
                    preventLink(e);
                    setIsOpenLivePopup(true);
                  }
                }}
              >
                <div className='d-flex'>
                  <div className={`sidebar-item-container sidebar-play`}>
                    <span className={'political-updates-nav-icon icon-play inline-block '} />
                  </div>
                  <span>Parliament live</span>
                </div>
              </NavLink>
            </li>
          </div>
        </>
      )}
      {getAppSite() === 'usa' && generalAllowedDashboardUsers && (
        <div className={`d-block mb-3 ${showDashboard ? '' : ' mt-2'}`}>
          <li>
            <NavLink
              to='/monitor-inbox/daily-briefing'
              className={() =>
                `sidebar-item ${location.pathname.includes('/daily-briefing') ? 'active' : ''} ${rateUpdatesMode ? 'light-50-text cursor-auto' : ''}`
              }
              onClick={(e) => {
                if (rateUpdatesMode) {
                  preventLink(e);
                }
                if (closeMenu) {
                  closeMenu();
                }
              }}
            >
              <div className='d-flex'>
                <div className={`sidebar-item-container sidebar-briefing`}>
                  <span className={'political-updates-nav-icon icon-briefing inline-block '} />
                </div>
                <span>Your daily briefing</span>
              </div>
            </NavLink>
          </li>
        </div>
      )}
      <div
        className={`flex-centered specific-y-margin-8 ${['global', 'usa'].includes(getAppSite()) ? 'specific-top-margin-lg-20' : ''} specific-bottom-margin-lg-10`}
      >
        <h4 className={`title-h5-m-bold pl-3  mb-0`}>Resources</h4>
        <MarkAsReadButton />
      </div>
      {items.map((item) => {
        const {
          name,
          unseenNumber,
          hideOnInbox,
          hideOnSearch,
          icon,
          type,
          nameForUnseenNumber,
          hidden,
          label,
          padlock,
          onClick,
        } = item;
        let nameNormalize = normalize(name);
        let unseenCondition = list !== null && unseenNumbersCurrentList;
        let unseenConditionForTweets = unseenCondition;
        let inboxCondition = !inboxSection || (inboxSection && !hideOnInbox);
        let searchResultsCondition =
          !searchResultsSection || !hideOnSearch || (hideOnSearch && getUrlParam('search')) || getUrlParam('or');
        let showItem = inboxCondition && searchResultsCondition && !hidden;
        if (item.name === 'Tweets') {
          unseenConditionForTweets = unseenCondition && enableTwitter;
        }
        let unseenNumberToRender = 0;
        if (unseenConditionForTweets) {
          unseenNumber.forEach((item) => {
            let propertyNumber = unseenNumbersCurrentList[item];
            if (propertyNumber) {
              unseenNumberToRender += propertyNumber;
            }
          });
        }
        let totalNumber = inboxSection
          ? unseenNumberToRender
          : totalNumbers[
              nameForUnseenNumber ??
                transformToPascalCase(
                  name === 'Calendar' ? 'Upcoming events' : name === 'Social media' ? 'Tweets' : name
                )
            ];
        const iconClass = icon ?? normalize(TransformFromPascalCase(type ?? name));
        const iconClassName = `political-updates-nav-icon icon-${iconClass} inline-block `;

        return (
          <li key={nameNormalize} style={{ display: showItem ? 'block' : 'none' }}>
            <NavLink
              to={locationDescriptor(`/${nameNormalize}`)}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              onClick={(e) => {
                if (onClick) {
                  preventLink(e);
                  onClick(e);
                }
                if (closeMenu) {
                  closeMenu();
                }
              }}
            >
              <div className='d-flex'>
                <div className={`sidebar-item-container sidebar-${iconClass}`}>
                  <span className={iconClassName} />
                </div>
                <span>{getName({ property: label ?? name })}</span>
              </div>
              {padlock ? (
                <span className='icon-padlock paragraph-p3 position-absolute top-11 right-20' />
              ) : (
                <>
                  {totalNumber && totalNumber > 0 ? (
                    <NotificationCount inboxSection={inboxSection} unseenNumberToRender={totalNumber} />
                  ) : (
                    ''
                  )}
                </>
              )}
            </NavLink>
          </li>
        );
      })}
      <ParliamentLivePopup isOpen={isOpenLivePopup} setIsOpen={setIsOpenLivePopup} />
      <DisabledTwitterPopup isOpen={showTwitterPopup} setIsOpen={setShowTwitterPopup} />
    </>
  );
};

const NotificationCount = (props) => {
  const { unseenNumberToRender, inboxSection } = props;

  return (
    <div
      className={`notification-count`}
      data-tooltip-content={`${unseenNumberToRender >= 9999 ? '9,999+' : thousandCommas(unseenNumberToRender)} ${
        inboxSection ? 'unread update' : 'result'
      }${unseenNumberToRender === 1 ? '' : 's'}`}
      data-tooltip-id='general-tooltip'
    >
      {unseenNumberToRender > 99 ? '99+' : unseenNumberToRender}
    </div>
  );
};
export { SideBarItems };
export default DashboardSidebar;
